import { formatAud } from '@/pages/blueprint-lite/utils/formatters';

import type { Citizenship, TaxResidency, Warning } from '../types';

export interface WarningInput {
  isForeign: boolean;
  citizenship: Citizenship;
  hasPurchase: boolean;
  surplus: number;
  lvr: number;
  depositGap: number;
  totalRequired: number;
  stampDutyTotal: number;
  fees: number;
  purchasePrice: number;
  effectiveDeposit: number;
  taxResidency: TaxResidency;
}

export function buildWarnings(input: WarningInput): Warning[] {
  const warnings: Warning[] = [];

  if (input.isForeign) {
    warnings.push({
      severity: 'high',
      message:
        'Foreign national: stamp duty surcharge applies; lender options are restricted.',
    });
  }

  if (input.surplus < 0) {
    warnings.push({
      severity: 'high',
      message: `Negative surplus ${formatAud(input.surplus)}/mo — income cannot service this loan.`,
    });
  }

  if (input.lvr > 80 && input.hasPurchase) {
    const extra = Math.max(0, input.purchasePrice * 0.2 - input.effectiveDeposit);
    warnings.push({
      severity: 'medium',
      message: `LVR ${input.lvr.toFixed(1)}% above 80% — LMI required. Additional deposit ${formatAud(extra)} would remove LMI.`,
    });
  }

  if (input.depositGap < 0 && input.hasPurchase) {
    warnings.push({
      severity: 'medium',
      message: `Deposit shortfall ${formatAud(Math.abs(input.depositGap))}. Total needed ${formatAud(input.totalRequired)} (incl. stamp duty ${formatAud(input.stampDutyTotal)} + fees ${formatAud(input.fees)}).`,
    });
  }

  if (input.taxResidency === 'unsure') {
    warnings.push({
      severity: 'high',
      message:
        'Tax-residency status is unclear — confirm with an ODIN tax agent before settlement; this materially affects CGT, withholding, and marginal-rate treatment.',
    });
  }

  return warnings;
}
